// Viewer 2.0 side-panel state (isolated from the 1.0 SplitPanelStore).
//
// Tracks which panel is currently open in the v2 panel host. The Options menu
// opens panels; selecting the same panel again closes it (toggle). Kept minimal
// and v2-local so the rebuild does not couple to 1.0 state.

import { create } from "zustand";

/** The panels reachable from the Options menu (single-view C4 parity). */
export type ViewerPanelId =
    | "layers"
    | "sprites"
    | "zoom-to-tile"
    | "zoom-to-xy"
    | "geojson"
    | "bbox"
    | "feature-inspect";

export interface PanelStoreState {
    /** Currently open panel, or null when closed. */
    openPanel: ViewerPanelId | null;
    /** Free-form content payload for panels that need one (e.g. inspect features, sprite url). */
    panelData: unknown;
    /**
     * The engine handle, published by the viewer page so the shared Layout's
     * SplitPanel content can drive the map without prop-drilling through Layout.
     * `unknown` to avoid a react→state type cycle; consumers cast to MapEngineHandle.
     */
    engineHandle: unknown;
    /** Opens a panel (replacing any open one). */
    openPanelById: (id: ViewerPanelId, data?: unknown) => void;
    /** Toggles a panel: opens it, or closes it if already open. */
    togglePanel: (id: ViewerPanelId, data?: unknown) => void;
    /** Closes whatever panel is open. */
    closePanel: () => void;
    /** Updates the open panel's data without changing which panel is open. */
    setPanelData: (data: unknown) => void;
    /** Publishes the engine handle (viewer page calls this once the map is ready). */
    setEngineHandle: (handle: unknown) => void;
}

export const usePanelStore = create<PanelStoreState>((set, get) => ({
    openPanel: null,
    panelData: null,
    engineHandle: null,
    openPanelById: (id, data = null) => set({ openPanel: id, panelData: data }),
    togglePanel: (id, data = null) =>
        set(get().openPanel === id ? { openPanel: null, panelData: null } : { openPanel: id, panelData: data }),
    closePanel: () => set({ openPanel: null, panelData: null }),
    setPanelData: (data) => set({ panelData: data }),
    setEngineHandle: (handle) => set({ engineHandle: handle }),
}));
